import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeftIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CameraIcon,
  VideoCameraIcon,
  PlusIcon,
  XMarkIcon,
  EyeIcon,
  HomeIcon,
  ListBulletIcon,
  MapPinIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  TagIcon,
  PhoneIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  LockClosedIcon,
  DocumentTextIcon,
  CurrencyRupeeIcon
} from '@heroicons/react/24/outline';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { fetchOpportunity, updateOpportunity } from '../store/slices/opportunitiesSlice';
import { fetchUserRoleSKills } from '../store/slices/genericSlice';
import { CreateOpportunityRequest } from '../types';
import Button from '../components/common/Button';
import SkillSelector from '../components/common/SkillSelector';
import { SKILL_CATEGORIES } from '../data/skillCategories';
import BackButton from '../components/common/BackButton';
import toast from 'react-hot-toast';
import LocationPicker from '../components/common/LocationPicker';
import { resolveImageUrl } from '../utils/image';
import { compressImage } from '../utils/compression';
import { OPPORTUNITY_TYPES as OPP_CONSTANTS } from '../constants/opportunities';

// Import the same constants from PostOpportunityPage
const OPPORTUNITY_TYPES = [
  { value: OPP_CONSTANTS.PHOTOGRAPHER, label: 'Looking for Photographer', icon: CameraIcon, recipients: 'creator' },
  { value: OPP_CONSTANTS.VIDEOGRAPHER, label: 'Looking for Videographer', icon: VideoCameraIcon, recipients: 'creator' },
  { value: OPP_CONSTANTS.BOTH_CREATORS, label: 'Looking for Photographer & Videographer', icon: '📷', recipients: 'creator' },
  { value: OPP_CONSTANTS.MODEL, label: 'Looking for Model', icon: '✨', recipients: 'model' },
  { value: OPP_CONSTANTS.BOTH, label: 'Looking for Both (Creator & Model)', icon: '🤝', recipients: 'creator,model' },
  { value: OPP_CONSTANTS.CREATIVE_COLLABORATION, label: 'Creative Collaboration', icon: '🎨', recipients: 'all' },
];

const EXPERIENCE_LEVELS = [
  { value: 'any', label: 'Any Experience Level' },
  { value: 'beginner', label: 'Beginner (0-1 years)' },
  { value: 'intermediate', label: 'Intermediate (2-5 years)' },
  { value: 'professional', label: 'Professional (5+ years)' },
];

const COMPENSATION_TYPES = [
  { value: 'paid', label: 'Paid' },
  { value: 'tfp', label: 'Time for Portfolio (TFP)' },
  { value: 'negotiable', label: 'Negotiable' },
  { value: 'unpaid', label: 'Unpaid/Volunteer' },
];

const COMMON_TAGS = [
  'Portrait', 'Fashion', 'Wedding', 'Event', 'Product', 'Landscape',
  'Street', 'Editorial', 'Commercial', 'Fine Art', 'Beauty', 'Lifestyle',
  'Documentary', 'Sports', 'Travel', 'Architecture', 'Food', 'Conceptual'
];

const EditOpportunityPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { currentOpportunity, loading, submitting } = useAppSelector((state) => state.opportunities);
  const { user } = useAppSelector((state) => state.auth);
  const { skills } = useAppSelector((state) => state.generic);

  const [currentStep, setCurrentStep] = useState(1);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedSkills, setSelectedSkills] = useState<number[]>([]);
  
  const [formData, setFormData] = useState<CreateOpportunityRequest>({
    title: '',
    description: '',
    opportunity_type: OPP_CONSTANTS.PHOTOGRAPHER,
    location: '',
    start_date: '',
    end_date: '',
    is_flexible_dates: false,
    duration: '',
    budget: undefined,
    is_paid: false,
    compensation_type: 'tfp',
    experience_level: 'any',
    tags: [],
    requirements: '',
    contact_email: '',
    contact_phone: '',
    is_public: true,
    status: 'active',
    notify_on_update: true,
    images: [],
    latitude: 0,
    longitude: 0
  });

  const [tagInput, setTagInput] = useState('');
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [newImageFiles, setNewImageFiles] = useState<File[]>([]);
  const [newImagePreviews, setNewImagePreviews] = useState<string[]>([]);
  const [removedImages, setRemovedImages] = useState<string[]>([]);

  useEffect(() => {
    if (id) {
      dispatch(fetchOpportunity(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (currentOpportunity) {
      // Check if user is the creator
      if (currentOpportunity.creator_id !== user?.id) {
        toast.error('You can only edit your own opportunities');
        navigate('/opportunities/my');
        return;
      }

      // Populate form with existing data
      setFormData({
        title: currentOpportunity.title || '',
        description: currentOpportunity.description || '',
        opportunity_type: currentOpportunity.opportunity_type || OPP_CONSTANTS.PHOTOGRAPHER,
        location: currentOpportunity.location || '',
        start_date: currentOpportunity.start_date?.split('T')[0] || '',
        end_date: currentOpportunity.end_date?.split('T')[0] || '',
        is_flexible_dates: currentOpportunity.is_flexible_dates || false,
        duration: currentOpportunity.duration || '',
        budget: currentOpportunity.budget || undefined,
        is_paid: currentOpportunity.is_paid || false,
        compensation_type: currentOpportunity.compensation_type || 'tfp',
        experience_level: currentOpportunity.experience_level || 'any',
        tags: currentOpportunity.tags || [],
        requirements: currentOpportunity.requirements || '',
        contact_email: currentOpportunity.contact_email || '',
        contact_phone: currentOpportunity.contact_phone || '',
        is_public: currentOpportunity.is_public !== false,
        status: currentOpportunity.status || 'active',
        notify_on_update: currentOpportunity.notify_on_update !== false,
        images: currentOpportunity.images || [],
        latitude: formData.latitude,
        longitude: formData.longitude
      });

      setExistingImages(currentOpportunity.images_signed || []);
      
      // Sync tags to selectedSkills when opportunity loads and skills are available
      if (currentOpportunity.tags && skills.length > 0) {
        const initialSelectedSkills = currentOpportunity.tags
          .map(tag => skills.find(s => s.name === tag)?.id)
          .filter((id): id is number => id !== undefined);
        setSelectedSkills(initialSelectedSkills);
      }
    }
  }, [currentOpportunity, user, navigate, skills]);

  useEffect(() => {
    const recipients = OPPORTUNITY_TYPES.find(t => t.value === formData.opportunity_type)?.recipients || 'all';
    dispatch(fetchUserRoleSKills(recipients));
  }, [dispatch, formData.opportunity_type]);

  // Sync selectedSkills to formData.tags
  useEffect(() => {
    const tags = selectedSkills
      .map(id => skills.find(s => s.id === id)?.name)
      .filter((name): name is string => name !== undefined);
    
    setFormData(prev => ({ ...prev, tags }));
  }, [selectedSkills, skills]);

  const handleInputChange = (field: keyof CreateOpportunityRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTagAdd = (tag: string) => {
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
    }
    setTagInput('');
  };

  const handleTagRemove = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const handleImageUpload = async (files: FileList) => {
    if (files.length === 0) return;

    const allFiles = Array.from(files);
    
    // Compress files
    const compressedFiles = await Promise.all(
      allFiles.map(async (file) => {
        try {
          return await compressImage(file);
        } catch (error) {
          console.error("Compression failed", error);
          return file;
        }
      })
    );

    const newFiles = compressedFiles;

    if (newFiles.length === 0) return;

    const totalImages = existingImages.length - removedImages.length + newImageFiles.length + newFiles.length;

    if (totalImages > 5) {
      toast.error('Maximum 5 images allowed');
      return;
    }

    setNewImageFiles(prev => [...prev, ...newFiles]);
    
    const newPreviews = newFiles.map(file => URL.createObjectURL(file));
    setNewImagePreviews(prev => [...prev, ...newPreviews]);

    toast.success('Images added successfully');
  };

  const removeExistingImage = (imageUrl: string) => {
    setRemovedImages(prev => [...prev, imageUrl]);
  };

  const removeNewImage = (index: number) => {
    const newFiles = newImageFiles.filter((_, i) => i !== index);
    setNewImageFiles(newFiles);

    const newPreviews = newImagePreviews.filter((_, i) => i !== index);
    if (newImagePreviews[index]) {
      URL.revokeObjectURL(newImagePreviews[index]);
    }
    setNewImagePreviews(newPreviews);
  };

  const canProceedToNextStep = () => {
    switch (currentStep) {
      case 1:
        return formData.title.trim() && formData.description.trim() && formData.opportunity_type;
      case 2:
        return formData.location.trim();
      case 3:
        return !formData.is_paid || (formData.is_paid && formData.budget);
      case 4:
        return formData.contact_email?.trim();
      default:
        return true;
    }
  };

  const handleSubmit = async (isDraft = false) => {
    if (!id) return;

    try {
      const submitFormData = new FormData();
      
      // Append text fields
      submitFormData.append('title', formData.title);
      submitFormData.append('description', formData.description);
      submitFormData.append('opportunity_type', formData.opportunity_type);
      submitFormData.append('location', formData.location);
      
      if (formData.start_date) submitFormData.append('start_date', formData.start_date);
      if (formData.end_date) submitFormData.append('end_date', formData.end_date);
      
      submitFormData.append('is_flexible_dates', formData.is_flexible_dates.toString());
      submitFormData.append('duration', formData.duration || '');
      
      if (formData.budget !== undefined) submitFormData.append('budget', formData.budget.toString());
      
      submitFormData.append('is_paid', formData.is_paid.toString());
      submitFormData.append('compensation_type', formData.compensation_type || '');
      submitFormData.append('experience_level', formData.experience_level || '');
      
      // Append tags as array
      formData.tags.forEach((tag) => {
        submitFormData.append('tags[]', tag);
      });
      
      // Append new image files
      newImageFiles.forEach((file) => {
        submitFormData.append('images', file);
      });
      
      // Append removed images
      removedImages.forEach((imageUrl) => {
        submitFormData.append('remove_images[]', imageUrl);
      });
      
      submitFormData.append('requirements', formData.requirements || '');
      submitFormData.append('contact_email', formData.contact_email || '');
      submitFormData.append('contact_phone', formData.contact_phone || '');
      submitFormData.append('is_public', formData.is_public.toString());
      submitFormData.append('latitude', formData.latitude.toString());
      submitFormData.append('longitude', formData.longitude.toString());
      submitFormData.append('status', isDraft ? 'draft' : (formData.status || 'active'));
      submitFormData.append('notify_on_update', formData.notify_on_update?.toString() || 'true');

      await dispatch(updateOpportunity({ id, formData: submitFormData })).unwrap();

      // Clean up object URLs
      newImagePreviews.forEach(url => URL.revokeObjectURL(url));

      if (isDraft) {
        toast.success('Opportunity saved as draft!');
        navigate('/opportunities/my?tab=drafts');
      } else {

        navigate(`/opportunities/${id}`);
      }
    } catch (error: any) {
      console.error('Failed to update opportunity:', error);
      toast.error('Failed to update opportunity');
    }
  };

  // Step indicator component
  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3, 4].map((step) => (
        <div key={step} className="flex items-center">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
              step === currentStep
                ? 'bg-teal-600 text-white'
                : step < currentStep
                ? 'bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400'
                : 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
            }`}
          >
            {step}
          </div>
          {step < 4 && (
            <div
              className={`w-16 h-1 mx-2 ${
                step < currentStep ? 'bg-teal-100 dark:bg-teal-900/30' : 'bg-gray-200 dark:bg-gray-700'
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );

  // Step 1: Basic Information
  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Basic Information</h2>
        
        {/* Opportunity Type */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            What are you looking for? *
          </label>
          <div className="grid grid-cols-2 gap-4">
            {OPPORTUNITY_TYPES.map((type) => (
              <label
                key={type.value}
                className={`relative flex items-center p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                  formData.opportunity_type === type.value
                    ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <input
                  type="radio"
                  name="opportunity_type"
                  value={type.value}
                  checked={formData.opportunity_type === type.value}
                  onChange={(e) => handleInputChange('opportunity_type', e.target.value)}
                  className="sr-only"
                />
                <div className="flex items-center space-x-3">
                  {typeof type.icon === 'string' ? (
                    <span className="text-2xl">{type.icon}</span>
                  ) : (
                    <type.icon className="h-6 w-6" />
                  )}
                  <span className="font-medium text-gray-900 dark:text-white">{type.label}</span>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Title */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Opportunity Title *
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            placeholder="e.g., Fashion Creator Needed for Brand Shoot"
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
            required
          />
        </div>

        {/* Description */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Project Description *
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Describe your project in detail..."
            rows={6}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
            required
          />
        </div>
      </div>
    </div>
  );

  // Step 2: Location & Schedule
  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Location & Schedule</h2>
        
        {/* Location */}
        <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Location *
                    </label>
                    <LocationPicker
                        value={formData.location}
                        onChange={(location: any, coordinates: any) => {
                          handleInputChange('location', location)
                          handleInputChange('latitude', coordinates?.lat);
                          handleInputChange('longitude', coordinates?.lng);
                        // Optionally store coordinates if needed
                        if (coordinates) {
                            // console.log('Selected coordinates:', coordinates);
                        }
                        }}
                        placeholder="Enter your city or location..."
                        className="w-full"
                    />
                </div>

        {/* Duration */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Expected Duration
          </label>
          <input
            type="text"
            value={formData.duration}
            onChange={(e) => handleInputChange('duration', e.target.value)}
            placeholder="e.g., 2-3 hours, Half day, Full day"
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
          />
        </div>

        {/* Dates */}
        {!formData.is_flexible_dates && (<div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preferred Start Date
            </label>
            <div className="relative">
              <CalendarDaysIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => handleInputChange('start_date', e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preferred End Date
            </label>
            <div className="relative">
              <CalendarDaysIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => handleInputChange('end_date', e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
        </div>)}

        {/* Flexible Dates */}
        <div className="mb-6">
          <label className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={formData.is_flexible_dates}
              onChange={(e) => handleInputChange('is_flexible_dates', e.target.checked)}
              className="rounded border-gray-300 text-teal-600 focus:ring-teal-500 dark:bg-gray-700 dark:border-gray-600"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">I'm flexible with dates</span>
          </label>
        </div>
      </div>
    </div>
  );

  // Step 3: Compensation & Requirements
  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Compensation & Requirements</h2>
        
        {/* Compensation Type */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Compensation Type *
          </label>
          <div className="grid grid-cols-2 gap-3">
            {COMPENSATION_TYPES.map((type) => (
              <label
                key={type.value}
                className={`relative flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                  formData.compensation_type === type.value
                    ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <input
                  type="radio"
                  name="compensation_type"
                  value={type.value}
                  checked={formData.compensation_type === type.value}
                  onChange={(e) => {
                    handleInputChange('compensation_type', e.target.value);
                    handleInputChange('is_paid', e.target.value === 'paid');
                  }}
                  className="sr-only"
                />
                <span className="text-sm font-medium text-gray-900 dark:text-white">{type.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Budget */}
        {formData.is_paid && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Budget Range *
            </label>
            <div className="relative">
              <CurrencyRupeeIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="number"
                value={formData.budget || ''}
                onChange={(e) => handleInputChange('budget', e.target.value ? parseFloat(e.target.value) : undefined)}
                placeholder="Enter your budget"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                required={formData.is_paid}
              />
            </div>
          </div>
        )}

        {/* Experience Level */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Experience Level Required
          </label>
          <select
            value={formData.experience_level}
            onChange={(e) => handleInputChange('experience_level', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            {EXPERIENCE_LEVELS.map((level) => (
              <option key={level.value} value={level.value}>
                {level.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );

  // Step 4: Additional Details (same as before but with all fields)
  const renderStep4 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Additional Details</h2>
        
        {/* Tags section */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tags & Specializations
          </label>
          
          {/* Common Tags */}
          <div className="mb-3">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Popular tags:</p>
            <div className="flex flex-wrap gap-2">
              {COMMON_TAGS.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => handleTagAdd(tag)}
                  disabled={formData.tags.includes(tag)}
                  className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                    formData.tags.includes(tag)
                      ? 'bg-teal-100 text-teal-700 border-teal-200 dark:bg-teal-900/30 dark:text-teal-400 dark:border-teal-800 cursor-not-allowed'
                      : 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Tag Input */}
          <div className="flex space-x-2">
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleTagAdd(tagInput))}
              placeholder="Add custom tag"
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => handleTagAdd(tagInput)}
              disabled={!tagInput || formData.tags.includes(tagInput)}
            >
              Add
            </Button>
          </div>

          {/* Selected Tags */}
          {formData.tags.length > 0 && (
            <div className="mt-3">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Selected tags:</p>
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-3 py-1 text-sm bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400 rounded-full"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleTagRemove(tag)}
                      className="ml-2 text-teal-500 hover:text-teal-700"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Specializations */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Specializations
          </label>
          <SkillSelector
            selectedSkillIds={selectedSkills}
            onChange={(values) => setSelectedSkills(values)}
            options={skills}
            placeholder="Select specializations..."
            maxSkills={10}
            categories={SKILL_CATEGORIES}
          />
        </div>

        {/* Requirements */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Special Requirements
          </label>
          <textarea
            value={formData.requirements}
            onChange={(e) => handleInputChange('requirements', e.target.value)}
            placeholder="Any special equipment, wardrobe, skills, or other requirements..."
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
          />
        </div>

        {/* Images Section */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Reference Images
          </label>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Upload mood boards, reference photos, or examples (max 5 images)
          </p>

          {/* Existing Images */}
          {existingImages.filter(img => !removedImages.includes(img)).length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Current Images:</p>
              <div className="grid grid-cols-5 gap-2">
                {existingImages
                  .filter(img => !removedImages.includes(img))
                  .map((imageUrl, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={resolveImageUrl(imageUrl)}
                        alt={`Current ${index + 1}`}
                        className="w-full h-20 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removeExistingImage(imageUrl)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ×
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* New Images Preview */}
          {newImagePreviews.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">New Images:</p>
              <div className="grid grid-cols-5 gap-2">
                {newImagePreviews.map((previewUrl, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={previewUrl}
                      alt={`New ${index + 1}`}
                      className="w-full h-20 object-cover rounded-lg border-2 border-blue-200"
                    />
                    <button
                      type="button"
                      onClick={() => removeNewImage(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload New Images */}
          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => e.target.files && handleImageUpload(e.target.files)}
              className="hidden"
              id="image-upload"
              disabled={existingImages.length - removedImages.length + newImageFiles.length >= 5}
            />
            <label 
              htmlFor="image-upload" 
              className={`cursor-pointer ${
                existingImages.length - removedImages.length + newImageFiles.length >= 5 
                  ? 'opacity-50 cursor-not-allowed' 
                  : ''
              }`}
            >
              <CameraIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                {existingImages.length - removedImages.length + newImageFiles.length >= 5
                  ? 'Maximum 5 images reached'
                  : 'Click to add more images'
                }
              </p>
            </label>
          </div>
        </div>

        {/* Contact Information */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contact Email
            </label>
            <div className="relative">
              <EnvelopeIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="email"
                value={formData.contact_email}
                onChange={(e) => handleInputChange('contact_email', e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contact Phone (Optional)
            </label>
            <div className="relative">
              <PhoneIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="tel"
                value={formData.contact_phone}
                onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
              />
            </div>
          </div>
        </div>

        {/* Visibility */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Visibility
          </label>
          <div className="space-y-3">
            <label className="flex items-start space-x-3">
              <input
                type="radio"
                name="visibility"
                checked={formData.is_public}
                onChange={() => handleInputChange('is_public', true)}
                className="mt-1 text-teal-600 focus:ring-teal-500"
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <GlobeAltIcon className="h-5 w-5 text-gray-400" />
                  <span className="font-medium text-gray-900 dark:text-white">Public</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Anyone can discover and apply to this opportunity
                </p>
              </div>
            </label>
            
            <label className="flex items-start space-x-3">
              <input
                type="radio"
                name="visibility"
                checked={!formData.is_public}
                onChange={() => handleInputChange('is_public', false)}
                className="mt-1 text-teal-600 focus:ring-teal-500"
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <LockClosedIcon className="h-5 w-5 text-gray-400" />
                  <span className="font-medium text-gray-900 dark:text-white">Private</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Only people you share the link with can see this opportunity
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Status */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Status
          </label>
          <select
            value={formData.status}
            onChange={(e) => handleInputChange('status', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="active">Active</option>
            <option value="draft">Draft</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        {/* Notification Toggle */}
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <label className="flex items-start space-x-3">
            <input
              type="checkbox"
              checked={formData.notify_on_update}
              onChange={(e) => handleInputChange('notify_on_update', e.target.checked)}
              className="mt-1 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
            />
            <div>
              <span className="font-medium text-blue-900 dark:text-blue-300">Notify subscribers about this update</span>
              <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                People who viewed or applied to this opportunity will receive a notification about your changes.
              </p>
            </div>
          </label>
        </div>
      </div>
    </div>
  );

  // Clean up object URLs on component unmount
  useEffect(() => {
    return () => {
      newImagePreviews.forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  if (loading) {
    return (
      <div className="w-full mx-auto p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!currentOpportunity) {
    return (
      <div className="w-full mx-auto p-6 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Opportunity not found</h1>
        <Button onClick={() => navigate('/opportunities/my')}>
          Back to My Opportunities
        </Button>
      </div>
    );
  }

  return (
    <div className="w-full mx-auto p-6 h-full overflow-y-auto px-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-4 mb-4">
          <BackButton />
          
          <div className="h-6 w-px bg-gray-300"></div>
          
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm">
            <button 
              onClick={() => navigate('/dashboard')}
              className="text-gray-500 hover:text-gray-700"
            >
              <HomeIcon className="h-4 w-4" />
            </button>
            <span className="text-gray-400">/</span>
            <button 
              onClick={() => navigate('/opportunities')}
              className="text-gray-500 hover:text-gray-700"
            >
              Opportunities
            </button>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900 dark:text-white">Edit</span>
          </nav>
        </div>
        
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Edit Opportunity</h1>
        <p className="text-gray-600 dark:text-gray-400">
          Update your opportunity details. Subscribers will be notified of changes if enabled.
        </p>
      </div>

      {/* Step Indicator */}
      {renderStepIndicator()}

      {/* Form */}
      <motion.div
        key={currentStep}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6"
      >
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}
      </motion.div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <div className="flex space-x-3">
          {currentStep > 1 && (
            <Button
              variant="outline"
              onClick={() => setCurrentStep(currentStep - 1)}
            >
              Previous
            </Button>
          )}
        </div>

        <div className="flex space-x-3">
          <Button
            variant="ghost"
            onClick={() => handleSubmit(true)}
            disabled={submitting}
          >
            Save as Draft
          </Button>
          
          {showPreview && (
            <Button
              variant="outline"
              onClick={() => setShowPreview(!showPreview)}
            >
              <EyeIcon className="h-4 w-4 mr-2" />
              Preview
            </Button>
          )}

          {currentStep < 4 ? (
            <Button
              variant="primary"
              onClick={() => setCurrentStep(currentStep + 1)}
              disabled={!canProceedToNextStep()}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={() => handleSubmit(false)}
              loading={submitting}
              disabled={!canProceedToNextStep()}
            >
              Update Opportunity
            </Button>
          )}
        </div>
      </div>

      {/* Quick Action Buttons
      {currentStep > 1 && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600 mb-3">Quick actions:</p>
          <div className="flex space-x-3">
            <button
              onClick={() => navigate('/opportunities')}
              className="text-sm text-teal-600 hover:text-teal-700 flex items-center space-x-1"
            >
              <ListBulletIcon className="h-4 w-4" />
              <span>Browse Opportunities</span>
            </button>
            <button
              onClick={() => navigate('/opportunities/my')}
              className="text-sm text-teal-600 hover:text-teal-700 flex items-center space-x-1"
            >
              <DocumentTextIcon className="h-4 w-4" />
              <span>My Opportunities</span>
            </button>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default EditOpportunityPage;
